import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Home, Send, Sparkles, Plus, Trash2, MessageSquare, Bot, User as UserIcon, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import apiClient from '@/services/api';

const nannoAPI = {
  listSessions: () => apiClient.get('/nanno/sessions'),
  createSession: (title) => apiClient.post('/nanno/sessions', { title }),
  getMessages: (id) => apiClient.get(`/nanno/sessions/${id}/messages`),
  deleteSession: (id) => apiClient.delete(`/nanno/sessions/${id}`),
};

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Nanno = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [sessions, setSessions] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [streamBuffer, setStreamBuffer] = useState('');
  const scrollRef = useRef(null);

  const loadSessions = useCallback(async () => {
    try {
      const { data } = await nannoAPI.listSessions();
      setSessions(data.sessions || []);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { loadSessions(); }, [loadSessions]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, streamBuffer]);

  const openSession = async (id) => {
    setActiveId(id);
    try {
      const { data } = await nannoAPI.getMessages(id);
      setMessages(data.messages || []);
    } catch {
      toast.error('Failed to load session');
    }
  };

  const startNewChat = () => {
    setActiveId(null);
    setMessages([]);
    setStreamBuffer('');
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await nannoAPI.deleteSession(id);
      setSessions((s) => s.filter((x) => x.session_id !== id));
      if (activeId === id) startNewChat();
    } catch { toast.error('Delete failed'); }
  };

  const handleSend = async () => {
    const text = input.trim();
    if (!text || streaming) return;
    setInput('');
    const userMsg = { role: 'user', content: text, created_at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setStreaming(true);
    setStreamBuffer('');

    try {
      const res = await fetch(`${API_BASE}/nanno/message`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ session_id: activeId, message: text }),
      });
      if (!res.ok || !res.body) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = '';
      let currentSession = activeId;
      let buffered = '';
      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffered += decoder.decode(value, { stream: true });
        const events = buffered.split('\n\n');
        buffered = events.pop() || '';
        for (const evt of events) {
          const lines = evt.split('\n');
          let evtName = 'message';
          let dataStr = '';
          for (const ln of lines) {
            if (ln.startsWith('event:')) evtName = ln.slice(6).trim();
            else if (ln.startsWith('data:')) dataStr += ln.slice(5).trim();
          }
          if (evtName === 'session') { currentSession = dataStr; setActiveId(dataStr); }
          else if (evtName === 'error') { toast.error(dataStr); }
          else if (evtName === 'done') { /* handled below */ }
          else {
            acc += dataStr.replace(/\\n/g, '\n');
            setStreamBuffer(acc);
          }
        }
      }
      setMessages((m) => [...m, { role: 'assistant', content: acc, created_at: new Date().toISOString() }]);
      setStreamBuffer('');
      if (currentSession && !sessions.find((s) => s.session_id === currentSession)) {
        loadSessions();
      }
    } catch (err) {
      toast.error(err.message || 'Chat failed');
    } finally {
      setStreaming(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#FDFCFB] via-white to-purple-50/40">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-stone-200/50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-[#C53030]"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center shadow-sm">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <h1 className="text-xl font-bold text-[#1C1917]">Nanno</h1>
            <span className="text-xs text-stone-500 px-2 py-0.5 bg-purple-50 rounded-full">AI Assistant · Claude Haiku 4.5</span>
          </div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-6 grid grid-cols-1 lg:grid-cols-4 gap-6">
        <aside className="lg:col-span-1 space-y-3">
          <Button data-testid="nanno-new-chat" onClick={startNewChat} className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white">
            <Plus className="w-4 h-4 mr-2" /> New chat
          </Button>
          <div className="space-y-1">
            {sessions.length === 0 ? (
              <p className="text-xs text-stone-500 px-2 py-3">No chats yet. Start a conversation below.</p>
            ) : (
              sessions.map((s) => (
                <div
                  key={s.session_id}
                  data-testid={`nanno-session-${s.session_id}`}
                  onClick={() => openSession(s.session_id)}
                  className={`flex items-center justify-between px-3 py-2 rounded-md cursor-pointer group text-sm ${activeId === s.session_id ? 'bg-purple-100 text-purple-900' : 'hover:bg-stone-100 text-stone-700'}`}
                >
                  <div className="flex items-center gap-2 truncate">
                    <MessageSquare className="w-3.5 h-3.5 flex-shrink-0" />
                    <span className="truncate">{s.title || 'Untitled'}</span>
                  </div>
                  <button data-testid={`nanno-delete-${s.session_id}`} onClick={(e) => handleDelete(s.session_id, e)} className="opacity-0 group-hover:opacity-100 text-stone-400 hover:text-red-600">
                    <Trash2 className="w-3.5 h-3.5" />
                  </button>
                </div>
              ))
            )}
          </div>
        </aside>

        <section className="lg:col-span-3">
          <Card className="p-0 border border-stone-200 flex flex-col h-[75vh] overflow-hidden">
            <div ref={scrollRef} data-testid="nanno-scroll" className="flex-1 overflow-y-auto p-6 space-y-4">
              {messages.length === 0 && !streamBuffer && (
                <div className="text-center py-16 text-stone-500">
                  <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 mx-auto mb-4 flex items-center justify-center shadow-lg">
                    <Sparkles className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#1C1917] mb-2">Hi, I'm Nanno.</h3>
                  <p className="text-sm">Ask me anything about lesson planning, NCFSE 2023 competencies, or curriculum design.</p>
                  <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-2 max-w-xl mx-auto">
                    {['Suggest a 5-day plan on Photosynthesis for Grade 7', 'What are NCFSE competencies for Class 5 Math?', 'Give me 3 innovative teaching ideas for Fractions', 'Write an exit card for Class 8 Civics'].map((s) => (
                      <button key={s} onClick={() => setInput(s)} className="text-xs text-left px-3 py-2 border border-stone-200 rounded-md hover:bg-stone-50">
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              {messages.map((m, i) => (
                <div key={`msg-${i}-${m.created_at}`} data-testid={`nanno-msg-${i}`} className={`flex gap-3 ${m.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${m.role === 'user' ? 'bg-[#C53030]' : 'bg-gradient-to-br from-purple-500 to-pink-500'}`}>
                    {m.role === 'user' ? <UserIcon className="w-4 h-4 text-white" /> : <Bot className="w-4 h-4 text-white" />}
                  </div>
                  <div className={`max-w-[75%] rounded-lg px-4 py-2 whitespace-pre-wrap text-sm ${m.role === 'user' ? 'bg-[#C53030] text-white' : 'bg-stone-100 text-stone-800'}`}>
                    {m.content}
                  </div>
                </div>
              ))}
              {streamBuffer && (
                <div className="flex gap-3">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center flex-shrink-0">
                    <Bot className="w-4 h-4 text-white" />
                  </div>
                  <div className="max-w-[75%] rounded-lg px-4 py-2 whitespace-pre-wrap text-sm bg-stone-100 text-stone-800">
                    {streamBuffer}<span className="animate-pulse">▍</span>
                  </div>
                </div>
              )}
            </div>
            <div className="border-t border-stone-200 p-4 flex gap-2">
              <Input
                data-testid="nanno-input"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } }}
                placeholder="Message Nanno..."
                disabled={streaming}
                className="flex-1"
              />
              <Button data-testid="nanno-send" onClick={handleSend} disabled={streaming || !input.trim()} className="bg-gradient-to-r from-purple-600 to-pink-600 text-white">
                {streaming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
              </Button>
            </div>
          </Card>
        </section>
      </main>
    </div>
  );
};

export default Nanno;
